import { Box, Link, Typography } from '@mui/material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { nightOwl } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import Button from '@/components/Buttons/Button';
import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import useUserDetails from '@/hooks/useUserDetails';
import copyToClipboard from '@/utils/copyToClipboard';

import usePythonPreview from './usePythonPreview';

const PythonPreviewScreen = ({ capturePosthogEvent }: { capturePosthogEvent: () => void }) => {
  const { loading, error, fileContent, downloadFile } = usePythonPreview(capturePosthogEvent);

  const { isUserBasic, isUserExpired } = useUserDetails();

  const docsLink = (
    <Typography sx={{ p: 2, fontSize: '12px' }}>
      For more information on the GX SDK, please visit our{' '}
      <Link
        href='https://docs.g-x.co/#python-sdk'
        rel='noopener noreferrer'
        target='_blank'
        underline='hover'
      >
        documentation site
      </Link>
      .
    </Typography>
  );

  if (isUserBasic || isUserExpired)
    return (
      <>
        <UpgradeToPremiumButton
          sx={{ ml: 2 }}
          type='upgrade'
        />
        {docsLink}
      </>
    );

  return (
    <Box sx={{ minWidth: 470, maxWidth: 735 }}>
      <Box sx={{ ml: 2, display: 'flex' }}>
        <Button
          disabled={!fileContent || loading}
          size='small'
          sx={{ mr: 2 }}
          variant='contained'
          onClick={() => {
            capturePosthogEvent();
            copyToClipboard(fileContent, 'Code snippet copied to clipboard!');
          }}
        >
          Copy to clipboard
        </Button>
        <Button
          disabled={!fileContent || loading}
          size='small'
          variant='contained'
          onClick={downloadFile}
        >
          Download
        </Button>
      </Box>
      {error && (
        <Typography
          color='error'
          sx={{ p: 2, fontSize: '14px' }}
        >
          An error occurred while fetching data. Please try again.
        </Typography>
      )}
      {loading ? (
        <Typography sx={{ p: 2, fontSize: '14px' }}>
          Retrieving your data, please wait...
        </Typography>
      ) : (
        <Box
          sx={{
            whiteSpace: 'pre',
            fontFamily: 'monospace',
            fontSize: '14px',
            mt: 3
          }}
        >
          {docsLink}
          <SyntaxHighlighter
            customStyle={{ backgroundColor: '#1A224D', margin: 0, height: 380 }}
            language='python'
            style={nightOwl}
          >
            {fileContent}
          </SyntaxHighlighter>
        </Box>
      )}
    </Box>
  );
};

export default PythonPreviewScreen;
